import { Inject, Injectable, OnDestroy } from '@angular/core';
import { assert, setDifference, setIntersection } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { intersection } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { selectionToArray } from '../../../../cross-team-shared/misc-utils';
import { keys } from '../../../../cross-team-shared/misc-utils/keys';
import { IN_PROGRESS_INDEX } from '../../../../team-apis/core/reserved-shape-indexes';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../../team-apis/core/shell-connector';
import { CuboidState3D } from '../../../state/cuboid-state-3d';
import { PointCloudContentStore } from '../../../state/point-cloud-content-store';
import { ObjectWith } from '../../../utils/misc-utils';
import { SceneControllerService } from '../../scene-controller.service';
import { connectorMaterial, CuboidRouteHelper } from './cuboid-route-helper';
import { RouteMarker } from './route-marker';

@Injectable()
export class CuboidRouteService
  extends InjectableSuperclass
  implements OnDestroy
{
  private cuboidRouteHelpers = new Map<number, CuboidRouteHelper>();

  constructor(
    private store: PointCloudContentStore,
    private sceneControllerService: SceneControllerService,
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
  ) {
    super();

    if (this.shellConnector.hasFeatureFlag('route-way')) {
      RouteMarker.instantiateResources();
      this.setUpSubscriptions();
    }
  }

  ngOnDestroy() {
    if (this.shellConnector.hasFeatureFlag('route-way')) {
      this.removeRouteHelpers(Array.from(this.cuboidRouteHelpers.keys()));
      RouteMarker.dispose();
      connectorMaterial.dispose();
    }
  }

  private setUpSubscriptions() {
    this.subscribeTo(
      combineLatest([
        this.store('render')('selection').$.pipe(
          map(selectionToArray),
          filter((selection) => selection[0] !== IN_PROGRESS_INDEX),
        ),
        this.store('render')('viewMode').$,
        this.store('render')('showRoute').$,
        this.store('render')('cuboids').$,
        this.store('sensorRendererState')('sensorFrames')('length').$,
        this.store('render')('frameNumber').$,
      ]),
      ([selection, viewMode, showRoute, cuboids, sequenceLength]) => {
        let updatedRouteIds: number[] = [];

        if (viewMode !== 'none' && showRoute) {
          updatedRouteIds = intersection(selection, keys(cuboids));
        }

        this.updateRoutes(new Set(updatedRouteIds), cuboids, sequenceLength);
      },
    );
  }

  private updateRoutes(
    updatedRouteIds: Set<number>,
    shapes: ObjectWith<CuboidState3D>,

    sequenceLength: number,
  ) {
    const existingRouteIds = new Set(this.cuboidRouteHelpers.keys());

    const routesToAdd = setDifference(updatedRouteIds, existingRouteIds);
    const routesToUpdate = setIntersection(existingRouteIds, updatedRouteIds);
    const routesToRemove = setDifference(existingRouteIds, updatedRouteIds);

    this.createRouteHelpers(Array.from(routesToAdd), shapes, sequenceLength);
    this.updateRouteHelpers(Array.from(routesToUpdate), shapes);
    this.removeRouteHelpers(Array.from(routesToRemove));
  }

  private createRouteHelpers(
    indexes: number[],
    shapes: ObjectWith<CuboidState3D>,
    sequenceLength: number,
  ) {
    indexes.forEach((cuboidIndex) => {
      const cuboidRouteHelper = new CuboidRouteHelper(
        sequenceLength,
        this.store.state().render.frameNumber,
        cuboidIndex,
        shapes[cuboidIndex].key_locations,
      );
      this.sceneControllerService.addToScene(cuboidRouteHelper.getMeshes());
      this.cuboidRouteHelpers.set(cuboidIndex, cuboidRouteHelper);
    });
  }

  private updateRouteHelpers(
    indexes: number[],
    shapes: ObjectWith<CuboidState3D>,
  ) {
    indexes.forEach((cuboidIndex) => {
      const frameNumber = this.store.state().render.frameNumber;
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      const locations = shapes[cuboidIndex]?.key_locations;

      // Deleting a shape also triggers a change to selection, which can cause
      // this to be called for a shape that no longer exists before the helper
      // is removed.
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      if (!locations) {
        return;
      }

      const helper = this.cuboidRouteHelpers.get(cuboidIndex);
      assert(helper, 'Helper should have been created on selection');

      this.sceneControllerService.removeFromScene(helper.getMeshes());
      helper.updateLocations(locations, frameNumber);
      this.sceneControllerService.addToScene(helper.getMeshes());
    });
  }

  private removeRouteHelpers(indexes: number[]) {
    indexes.forEach((cuboidIndex) => {
      const helper = this.cuboidRouteHelpers.get(cuboidIndex);
      assert(helper, 'Helper should have been created on selection');

      this.sceneControllerService.removeFromScene(helper.getMeshes());
      helper.cleanUp();

      this.cuboidRouteHelpers.delete(cuboidIndex);
    });
  }
}
